/**
 * ScArtifactCard (026, US3) — scoping artifact display card. Supersedes
 * ScopingArtifactCard from spec 017. Built from scratch.
 *
 * Discriminates on payload.kind: decision | bets | assumptions | goals | scope | note.
 * draft=true → draft badge + HeroActionButtons for approve/adjust.
 * draft=false | undefined → read-only final state.
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ChatCard, HeroActionButtons, WidgetActionProps } from '../../widgets/chat-widget-ui';
import { SkBar, SkBtn } from '../../widgets/skeletons';
import { ScStateBadge } from './ScStateBadge';
import { humanizeType, capitalizeFirst, RISK_META, TYPE_PILL_STYLE, byRiskDesc } from './scLabels';
import type { ScArtifactCardProps } from '@shared/widget-schemas';

type ScArtifactCardComponentProps = ScArtifactCardProps & WidgetActionProps & {
  className?: string;
};

/**
 * Content-derived React keys for a list the CLIENT sorts (ANA-444) — the FALLBACK
 * used when a row carries no stable roster `id`.
 *
 * These lists are re-sorted by risk on every render AND replaced wholesale when
 * Cloud re-emits the card under the same `widget_id`. A positional (index) key
 * therefore binds a DOM node to a SLOT rather than to an item, so a reorder or an
 * insert silently hands one row's node — and any state, focus or transition
 * attached to it — to different content.
 *
 * ANA-936 threaded the stable roster `id` through to the `bets_with_assumptions`
 * kind (see {@link BetsWithAssumptionsBody}), which keys on it when present — this
 * function is what a group/item falls back to when it carries none: the legacy
 * `assumptions` kind (superseded, never populated by the materializer any more),
 * the 6a briefing_draft source (no roster id exists to send), and any card
 * persisted/hydrated before the `id` field existed. Nothing dedupes by text, so
 * exact duplicates remain possible: an occurrence counter keeps those distinct.
 * It is deliberately the count of PRIOR identical bases and not the absolute
 * index — an absolute index changes on every reorder, which is the bug.
 *
 * Exported for the one other client-sorted assumption list, in the superseded
 * {@link ScopingArtifactCard}, so both share a single definition. (`scLabels.ts`
 * would be the natural home if that file ever gains a non-label section.)
 */
export function contentKeys(bases: string[]): string[] {
  const seen = new Map<string, number>();
  return bases.map((base) => {
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return `${base}::${n}`;
  });
}

/** Key base for an assumption row — the two fields the row actually renders. */
export const assumptionKeyBase = (item: { text: string; risk?: string }): string => `${item.text}::${item.risk ?? ''}`;

const KIND_LABELS: Record<string, string> = {
  decision: 'Decision',
  bets: 'Bets',
  assumptions: 'Assumptions',
  bets_with_assumptions: "What we'll test",
  goals: "What we'll learn",
  scope: 'Scope',
  note: 'Note',
};

export function ScArtifactCard({
  payload,
  draft,
  titleOverride,
  actions,
  onAction,
  className,
}: ScArtifactCardComponentProps) {
  // bets↔assumptions-merge plan (MF3) — the legacy `bets`/`assumptions` kinds are
  // superseded by `bets_with_assumptions`; Cloud dismisses their old widget_id by
  // re-emitting them empty. Render nothing rather than an empty shell card.
  if (payload.kind === 'bets' && payload.items.length === 0) return null;
  if (payload.kind === 'assumptions' && payload.groups.length === 0) return null;

  const isDraft = draft === true;
  // Header pill state (shared ScStateBadge) — the canonical draft/confirmed
  // contract, uniform across ALL kinds (the old `CONFIRMABLE_KINDS` gate that
  // limited CONFIRMED to decision/scope is retired): DRAFT when draft===true,
  // CONFIRMED when draft===false, nothing when `draft` is OMITTED (an absent
  // flag must never read as CONFIRMED).
  const badgeDraft: boolean | undefined = draft;

  return (
    <ChatCard className={cn('flex flex-col gap-3 p-4', className)}>
      {/* Header row */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground font-mono text-[10.5px] font-bold uppercase tracking-wide">
          {/* `titleOverride` names where the content came from when that isn't the
              call itself (004-p10 — a decision seeded from project creation reads
              "Our Starting Point"). Cloud-set; the model never chooses it.
              Defensive fallback: never echo an unrecognised raw discriminator (e.g. a
              leaked "GoNoGo" outcome_type) as the header — fall back to a neutral
              label. The repair transform is the primary guard; this is belt-and-braces. */}
          {titleOverride ?? KIND_LABELS[payload.kind] ?? 'Captured'}
        </span>
        <div className="flex items-center gap-2">
          {/* The decision's project type sits up here, to the LEFT of the state badge. */}
          {payload.kind === 'decision' && payload.projectType && <TypePill label={payload.projectType} />}
          <ScStateBadge draft={badgeDraft} />
        </div>
      </div>

      {/* Payload body */}
      <ArtifactBody payload={payload} />

      {/* In-card actions — only in draft state */}
      {isDraft && actions && actions.length > 0 && (
        <div className="flex flex-wrap gap-2 border-t border-[var(--border)] pt-3">
          <HeroActionButtons actions={actions} onAction={onAction} />
        </div>
      )}
    </ChatCard>
  );
}

function ArtifactBody({ payload }: { payload: ScArtifactCardProps['payload'] }) {
  switch (payload.kind) {
    case 'decision':
      return (
        <div className="flex flex-col gap-2">
          {/* The intent is the anchor — larger + bold, like the bet on the
              assumptions card; project type sits in the header, rationale muted.
              The decision intent is sourced from the founder's free-text
              project-creation answer ("what are you trying to decide?"), which
              can run long — clamp it to 4 lines so a runaway answer never turns
              the card into a wall of text. Display-only: the full text is retained
              in the roster + the model prompt. `line-clamp-4` truncates on a line
              boundary with an ellipsis, responsive to card width (no character
              cap needed — a char cap can zero a no-space span or split a
              surrogate pair). */}
          <p className="text-foreground line-clamp-4 text-[14px] font-semibold leading-snug">{capitalizeFirst(payload.intent)}</p>
          {payload.rationale && (
            <p className="text-muted-foreground text-[12px] leading-relaxed">{capitalizeFirst(payload.rationale)}</p>
          )}
        </div>
      );

    case 'bets':
      return (
        <ol className="flex flex-col divide-y divide-[var(--border)]">
          {payload.items.map((item, i) => (
            <li key={i} className="flex gap-2.5 py-3 first:pt-2 last:pb-0">
              <NumberBadge n={i + 1} />
              <div className="flex flex-1 flex-col gap-1.5">
                <p className="text-foreground text-[13px] leading-relaxed">{capitalizeFirst(item.text)}</p>
                {/* Type pill (bet_type) only — mirrors the assumptions card. The
                    fuller rationale lives on the rail (Cloud-derived from the stored
                    capture); the model's inline widget provenance is not surfaced here
                    to avoid a worse-worded duplicate of the rail. */}
                {item.type && <TypePill label={item.type} />}
              </div>
            </li>
          ))}
        </ol>
      );

    case 'assumptions':
      return (
        <div className="flex flex-col gap-4">
          {payload.groups.map((group, gi) => {
            // Sort ONCE per render, then key off content — the numbering below still
            // uses the post-sort position (ANA-444).
            const items = [...group.items].sort(byRiskDesc);
            const itemKeys = contentKeys(items.map(assumptionKeyBase));
            return (
              <div key={gi} className="flex flex-col gap-1">
                {/* The parent bet is the anchor — a "Bet" pill centred against the bet
                    statement so the pill text sits in line with it. */}
                {group.bet && (
                  <div className="flex items-center gap-2">
                    <BetPill />
                    <p className="text-foreground flex-1 text-[13.5px] font-semibold leading-snug">{capitalizeFirst(group.bet)}</p>
                  </div>
                )}
                {/* Assumptions: numbered, ordered by risk (most severe first), with a
                    hairline divider + breathing room between each. */}
                <ol className="flex flex-col divide-y divide-[var(--border)]">
                  {items.map((item, ai) => (
                    <li key={itemKeys[ai]} className="flex gap-2.5 py-3 first:pt-2 last:pb-0">
                      <NumberBadge n={ai + 1} />
                      <div className="flex flex-1 flex-col gap-1.5">
                        <p className="text-foreground text-[12.5px] leading-relaxed">{capitalizeFirst(item.text)}</p>
                        {(item.type || item.risk) && (
                          <div className="flex flex-wrap items-center gap-1.5">
                            {item.type && <TypePill label={item.type} />}
                            {item.risk && <RiskChip risk={item.risk} />}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            );
          })}
        </div>
      );

    case 'bets_with_assumptions':
      return <BetsWithAssumptionsBody groups={payload.groups} />;

    case 'goals':
      return (
        <ol className="flex flex-col divide-y divide-[var(--border)]">
          {payload.items.map((goal, i) => (
            <li key={i} className="flex gap-2.5 py-3 first:pt-2 last:pb-0">
              <NumberBadge n={i + 1} />
              <div className="flex flex-1 flex-col gap-1.5">
                <p className="text-foreground text-[13px] leading-relaxed">{capitalizeFirst(goal.text)}</p>
                {goal.type && <TypePill label={goal.type} />}
              </div>
            </li>
          ))}
        </ol>
      );

    case 'scope':
      return (
        <div className="flex flex-col gap-2">
          {payload.summary && (
            <p className="text-foreground text-[13.5px] leading-relaxed">{capitalizeFirst(payload.summary)}</p>
          )}
          {payload.items && payload.items.length > 0 && (
            <ul className="flex flex-col gap-1">
              {payload.items.map((item, i) => (
                <li key={i} className="text-muted-foreground text-[12.5px]">
                  {i + 1}. {capitalizeFirst(item)}
                </li>
              ))}
            </ul>
          )}
        </div>
      );

    case 'note':
      return <p className="text-foreground text-[13.5px] italic leading-relaxed">{capitalizeFirst(payload.text)}</p>;

    default:
      // Defensive: an unrecognised kind must never fall through to `undefined`
      // (which crashes React with a "widget error"). Render nothing — the
      // scopingWidgetRepair transform substitutes/drops malformed cards upstream,
      // so this only guards a validation-bypassing path.
      return null;
  }
}

type BetsWithAssumptionsGroups = Extract<
  ScArtifactCardProps['payload'],
  { kind: 'bets_with_assumptions' }
>['groups'];

/**
 * `bets_with_assumptions` body. Stateful (unlike every other kind here) because a
 * confirmed bet's assumptions collapse by default with a chevron to re-expand —
 * everything else on this card is still driven purely by props.
 *
 * `manualExpanded` only ever records an explicit user toggle; a group with no
 * entry falls back to its status-derived default (draft ⇒ expanded, confirmed ⇒
 * collapsed). That means a bet that flips draft→confirmed on a later update
 * (no manual entry yet) auto-collapses, exactly like a freshly-confirmed bet —
 * the trigger is "confirmed", not "just confirmed this render".
 *
 * That state is keyed by the BET, never by the group's index (ANA-444): Cloud
 * replaces this card wholesale by `widget_id`, so a reordered or inserted group
 * would otherwise move the user's expand/collapse choice onto a different bet.
 *
 * The key is the group's stable roster `id` WHEN PRESENT (ANA-936), falling back
 * to a {@link contentKeys} content-derived key when absent (the 6a source, or a
 * card persisted/hydrated before `id` existed). Keying on identity rather than
 * content removes the ANA-444 trade-off entirely for the id-bearing case: keying
 * by TEXT alone survives a reorder but drops the user's choice when the model
 * REWORDS a bet (the entry orphans and the status-derived default returns — a
 * manually-expanded confirmed bet collapses); keying by INDEX would survive a
 * reword but bind the choice to a DIFFERENT bet on any reorder. With a roster id,
 * neither failure mode applies — the id is stable across both a reword AND a
 * reorder. The content-keyed fallback (silently showing one bet's expansion on
 * another is worse than losing the choice) still applies whenever no id is sent.
 *
 * The id is taken on trust, not assumed unique: `sc_artifact_card` is in
 * `CHAT_THREAD_WIDGET_IDS`, so this shape is model-tool-call-reachable, not
 * materializer-only, and nothing at this layer stops a duplicate or empty-string
 * id from arriving (pr-review P2, ANA-936). The resolved id-or-content key list is
 * therefore run through a SECOND {@link contentKeys} pass below before use — it
 * disambiguates any duplicate (same id twice, or two empty ids) into distinct React
 * keys, closing off the exact "silently showing one bet's expansion on another"
 * failure this comment warns against, without changing behaviour for the normal
 * unique-id case (a solo occurrence always resolves to the same suffix).
 */
function BetsWithAssumptionsBody({ groups }: { groups: BetsWithAssumptionsGroups }) {
  const [manualExpanded, setManualExpanded] = useState<Record<string, boolean>>({});
  const groupContentKeys = contentKeys(groups.map((g) => g.bet));
  // The id is Cloud-minted and unique by construction on the materializer path, but
  // this payload shape is also model-authored-tool-call-reachable (sc_artifact_card
  // is in CHAT_THREAD_WIDGET_IDS), so nothing guarantees it here. A second
  // contentKeys pass disambiguates any duplicate — including two groups sharing an
  // id, or two carrying no id at all — without disturbing the stable-identity keying
  // for the (overwhelmingly common) unique-id case: a solo occurrence of a base
  // always gets the same `::0` suffix, so reword/reorder stability is unaffected.
  const groupKeys = contentKeys(groups.map((g, i) => g.id ?? groupContentKeys[i]));

  return (
    <div className="flex flex-col gap-4">
      {groups.map((group, gi) => {
        const groupKey = groupKeys[gi];
        const isConfirmed = group.status === 'confirmed';
        const hasAssumptions = group.assumptions.length > 0;
        const expanded = manualExpanded[groupKey] ?? !isConfirmed;
        const showChevron = isConfirmed && hasAssumptions;
        const assumptions = [...group.assumptions].sort(byRiskDesc);
        const assumptionContentKeys = contentKeys(assumptions.map(assumptionKeyBase));
        // Same id-when-present, content-fallback-when-absent contract as groupKeys,
        // and the same second contentKeys pass to disambiguate a duplicate/empty id.
        const assumptionKeys = contentKeys(assumptions.map((a, i) => a.id ?? assumptionContentKeys[i]));

        return (
          <div key={groupKey} className="flex flex-col gap-2">
            {/* The bet is the group's anchor — numbered, like the legacy
                `bets`/`assumptions` cards' item numbering, rather than a repeated
                "Bet" pill. Its type sits BELOW the statement, not crowded onto the
                same line — that's what made the header row overflow/wrap badly on
                narrow widths. Once confirmed, the tick REPLACES the ordinal number
                (it's the bet's badge now, not an addition next to a type pill). */}
            {group.bet && (
              <div className="flex items-start gap-2.5">
                {isConfirmed ? <ScStateBadge draft={false} /> : <NumberBadge n={gi + 1} />}
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <p className="text-foreground text-[13.5px] font-semibold leading-snug">{capitalizeFirst(group.bet)}</p>
                  {group.type && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <TypePill label={group.type} />
                    </div>
                  )}
                </div>
                {showChevron && (
                  <button
                    type="button"
                    onClick={() =>
                      setManualExpanded((prev) => {
                        // Drop entries whose bet is no longer on the card before
                        // recording this toggle. Without the prune, a reworded bet
                        // leaves its old key behind forever and the map grows with
                        // every distinct bet text seen in the session.
                        const live = new Set(groupKeys);
                        const kept = Object.fromEntries(Object.entries(prev).filter(([k]) => live.has(k)));
                        return { ...kept, [groupKey]: !expanded };
                      })
                    }
                    aria-expanded={expanded}
                    aria-label={expanded ? 'Collapse assumptions' : 'Expand assumptions'}
                    className="mt-px flex size-[22px] shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-[var(--secondary)] hover:text-foreground"
                  >
                    <ChevronDown className={cn('size-4 transition-transform', !expanded && '-rotate-90')} />
                  </button>
                )}
              </div>
            )}

            {hasAssumptions && expanded && (
              <ul className="flex flex-col gap-2.5 pl-[26px]">
                {assumptions.map((item, ai) => (
                  <li key={assumptionKeys[ai]} className="flex gap-2">
                    <span className="mt-[7.5px] size-1 shrink-0 rounded-full bg-muted-foreground" aria-hidden="true" />
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <p className="text-foreground text-[12.5px] leading-relaxed">{capitalizeFirst(item.text)}</p>
                      {(item.type || item.risk) && (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {item.type && <TypePill label={item.type} />}
                          {item.risk && <RiskChip risk={item.risk} />}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Small numbered index badge for ordered artifact lists (1, 2, 3…). */
function NumberBadge({ n }: { n: number }) {
  return (
    <span className="mt-px inline-flex size-[18px] shrink-0 items-center justify-center rounded-full border border-[#7C3AED]/25 bg-[#7C3AED]/10 font-mono text-[9.5px] font-bold text-[#C084FC]">
      {n}
    </span>
  );
}

/** Structural "Bet" label pill — solid purple so it reads as the anchor, distinct
 *  from the lighter tinted metadata type pills. */
function BetPill() {
  return (
    <span
      className="shrink-0 rounded-full px-2 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-wide text-white"
      style={{ background: '#7C3AED' }}
    >
      Bet
    </span>
  );
}

/** Humanised type pill (bet_type / assumption_type / goal_type). Smaller by
 *  default (mobile-first) — full size only from `sm:` up, so a row of tags
 *  doesn't crowd/wrap on a phone-width card. */
function TypePill({ label }: { label: string }) {
  return (
    <span
      className="self-start rounded-full px-1.5 py-[1px] text-[9px] font-medium sm:px-2 sm:py-0.5 sm:text-[10.5px]"
      style={TYPE_PILL_STYLE}
    >
      {capitalizeFirst(humanizeType(label))}
    </span>
  );
}

/** Colour-coded assumption risk chip (low → critical). Same mobile-first sizing
 *  as {@link TypePill} so the two tag kinds stay visually consistent. */
function RiskChip({ risk }: { risk: 'low' | 'medium' | 'high' | 'critical' }) {
  const meta = RISK_META[risk];
  return (
    <span
      className="shrink-0 rounded-full px-1.5 py-[1px] text-[9px] font-semibold sm:px-2 sm:py-0.5 sm:text-[10.5px]"
      style={{ color: meta.color, background: meta.bg }}
    >
      {meta.label}
    </span>
  );
}

export function ScArtifactCardSkeleton({ className }: { className?: string }) {
  return (
    <ChatCard className={cn('flex flex-col gap-3 p-4', className)}>
      <div className="flex justify-between">
        <SkBar width="25%" height="10px" />
        <SkBar width="12%" height="10px" />
      </div>
      <SkBar width="90%" height="13px" />
      <SkBar width="75%" height="13px" />
      <div className="flex gap-2 pt-1">
        <SkBtn width="90px" />
        <SkBtn width="70px" />
      </div>
    </ChatCard>
  );
}
